import type { TileFacts, TileRow } from "@kundeninfo/api-client";
import type { ListenkachelKennzahl } from "@kundeninfo/ui/me-design";
import { formatDatum } from "../../format.js";
import { PAYMENT_STATE_TEXT, paymentLabel } from "../suppliers/paymentState.js";
import type { PaymentState } from "../suppliers/paymentState.js";
import { tageZwischen } from "../tickets/ticketFormat.js";
import { FACT_TEXT, MARKER_TEXT, ROW_TEXT, SUPPLIER_TEXT, levelStatusArt, moneyText } from "./texts.js";
import type { LevelWord } from "./texts.js";
import type { TileContext, TileMapping } from "./tileCatalog.js";
import { dayText, flagged, levelOf, markerText, nextDateFact, rowView } from "./tileRows.js";

/*
 * Die Belegkacheln (Rechnungen, Abrechnung, Angebote, Aufträge und die zwei Lieferantenkacheln): welche Kennzahlen unter dem Kopf
 * stehen und wie eine Zeile aus Beleg-Daten entsteht.
 * Der Zustand steht immer als Wort neben der Farbe (`levelWord`); die Sortierung kommt fertig vom Server.
 */

/** Ein Stück der zweiten Zeile, nur wenn der Wert da ist (leer zählt wie fehlend). */
const when = (value: string | null, text: (value: string) => string): string => (value === null || value === "" ? "" : text(value));

const sumFact = (label: string, f: TileFacts): ListenkachelKennzahl =>
  ({ beschriftung: label, wert: moneyText(f.sums ?? []) || FACT_TEXT.none });

/**
 * Der Zahlstatus einer Eingangsrechnung als Wort („überfällig seit 7 Tagen“, „fällig in 3 Tagen“, „offen“) — dasselbe Wort wie am
 * Kennzeichen der Lieferantenreiter. `TileRow.status` trägt den Zustand des Servers; ein unbekannter ergibt kein Wort.
 */
function paymentWord(r: TileRow, ctx: TileContext): string {
  const state = r.status;
  if (state === null || !isPaymentState(state)) return "";
  return paymentLabel({ state, daysLeft: r.date === null ? null : tageZwischen(ctx.today, r.date) });
}
const isPaymentState = (s: string): s is PaymentState => Object.hasOwn(PAYMENT_STATE_TEXT, s);

/** Der Zustand einer Eingangsrechnung: ohne Stufe keiner; mit Stufe das Zahlstatuswort, ohne Wort das Wort der Fälligkeit. */
function incomingState(r: TileRow, ctx: TileContext, word: string): LevelWord {
  if (r.level === null) return {};
  return word === "" ? levelOf("due", r, ctx) : { statusArt: levelStatusArt(r.level), status: word };
}

export const DOCUMENT_TILE_MAPPINGS: Record<"invoices" | "billing" | "supplierContracts" | "incomingInvoices" | "offers" | "orders",
    TileMapping> = {
  invoices: {
    facts: (f) => [flagged(FACT_TEXT.overdue, f.overdue, "fehler"), sumFact(FACT_TEXT.open, f)],
    row: (r, ctx) => rowView(r, moneyText(r.amounts), dayText(r, ctx) ?? ROW_TEXT.noDueDate,
      [when(r.secondaryDate, (day) => ROW_TEXT.invoiceFrom(formatDatum(day))), when(r.detail, ROW_TEXT.orderRef)],
      levelOf("due", r, ctx)),
  },
  billing: {
    facts: (f) => [flagged(FACT_TEXT.missed, f.missed, "fehler"), sumFact(FACT_TEXT.notBilled, f)],
    row: (r, ctx) => rowView(r, r.text, dayText(r, ctx), [moneyText(r.amounts), markerText(r)], levelOf("charge", r, ctx)),
  },
  // Lieferanten Strang E (Aufgabe 21). Verträge: Art · Titel · nächste Fälligkeit · Betrag; nur wenn der letzte Kündigungstag in
  // der Vorwarnzeit liegt (`level` gesetzt), sagt die zweite Zeile sein Datum — der Server liefert es für jeden Vertrag mit
  // Frist (Final-Fixrunde Teil C). Das Zustandswort nennt keine Tageszahl (die Schwelle ist einstellbar).
  supplierContracts: {
    facts: (f) => [sumFact(SUPPLIER_TEXT.monthlyCost, f), flagged(SUPPLIER_TEXT.noticeSoon, f.noticeSoon, "warnung")],
    row: (r, ctx) => rowView(r, r.text, dayText(r, ctx),
      [moneyText(r.amounts), r.date === null ? ROW_TEXT.noOpenCharge : "",
        r.level === null ? "" : when(r.secondaryDate, (day) => SUPPLIER_TEXT.noticeUntil(formatDatum(day)))],
      r.level === null ? {} : { statusArt: levelStatusArt(r.level), status: SUPPLIER_TEXT.noticeSoon }),
  },
  // Eingangsrechnungen: E-Nr. (der Server liefert die nackte Nummer) · Rechnungsnr. · Fälligkeit · Brutto, Zahlstatus.
  incomingInvoices: {
    facts: (f) => [flagged(FACT_TEXT.overdue, f.overdue, "fehler"), sumFact(SUPPLIER_TEXT.openGross, f)],
    row: (r, ctx) => {
      const word = paymentWord(r, ctx);
      return rowView({ ...r, reference: r.reference === null ? null : SUPPLIER_TEXT.entryNumber(r.reference) }, r.text,
        dayText(r, ctx) ?? ROW_TEXT.noDueDate, [moneyText(r.amounts), word], incomingState(r, ctx, word));
    },
  },
  offers: {
    facts: (f, ctx) => [nextDateFact(FACT_TEXT.nextDeadline, "deadline", f, ctx)],
    row: (r, ctx) => rowView(r, r.text, dayText(r, ctx), [moneyText(r.amounts), markerText(r),
      r.marker === "HISTORICAL" ? ROW_TEXT.historicalOffer : "", when(r.detail, ROW_TEXT.replaces)],
    levelOf("deadline", r, ctx)),
  },
  orders: {
    facts: (f) => [flagged(FACT_TEXT.withoutPlan, f.withoutPlan, "warnung")],
    row: (r, ctx) => {
      const withoutPlan = r.marker === "WITHOUT_PLAN";
      const charges = r.count ?? 0;
      const open = charges === 0 ? ROW_TEXT.noOpenCharge : ROW_TEXT.openCharges(charges);
      // Ein historischer Auftrag hat keine Abrechnung im System — keine Fälligkeiten, kein Zahlplan (471). Ohne Laufzeitende
      // steht dort nichts: „unbefristet“ behauptete eine Laufzeit, die das System nicht kennt (A-K3).
      const historical = r.marker === "HISTORICAL";
      const below = historical ? [MARKER_TEXT.HISTORICAL, ROW_TEXT.historicalOrder]
        : [withoutPlan ? MARKER_TEXT.WITHOUT_PLAN : open];
      return rowView(r, r.text, dayText(r, ctx) ?? (historical ? null : ROW_TEXT.unlimited), below,
        r.level === "WARN" && withoutPlan ? { statusArt: "warnung", status: MARKER_TEXT.WITHOUT_PLAN }
          : levelOf("end", r, ctx));
    },
  },
};
